import { Injectable } from "@angular/core";
import { BaseValidationResult, ValidationResult } from "@ha/ui/forms";
import { SetFormDirty } from "@ngxs/form-plugin";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";

import { ReplacementCardRequest } from "../models/replacement-card-request";
import { ReplacementCardRequestConfiguration } from "../models/replacement-card-request-configuration";
import { ReplacementCardRequestService } from "../services/replacement-card.service";
import { ReplacementCardRequestActions } from "./replacement-card.actions";

export interface ReplacementCardStateModel {
    form: {
        model: ReplacementCardRequest;
        dirty: boolean;
        status: string;
        errors: unknown;
    };
    result: BaseValidationResult;
    configuration: ReplacementCardRequestConfiguration;
}

const defaults: ReplacementCardStateModel = {
    form: {
        model: undefined,
        dirty: false,
        status: "",
        errors: {},
    },
    result: undefined,
    configuration: undefined,
};

@Injectable()
@State<ReplacementCardStateModel>({
    name: "replacementCard",
    defaults: defaults,
})
export class ReplacementCardState {
    @Selector()
    public static validationResult(state: ReplacementCardStateModel): ValidationResult {
        return state?.result?.validationResult;
    }

    @Selector()
    public static formSubmitSucceded(state: ReplacementCardStateModel): boolean {
        return state?.result?.success;
    }

    @Selector()
    public static agreementText(state: ReplacementCardStateModel): string {
        return state.configuration?.agreementText;
    }

    @Selector()
    public static countryCodes(state: ReplacementCardStateModel): string[] {
        return state.configuration?.countries;
    }

    constructor(private replacementCardRequestService: ReplacementCardRequestService) {}

    @Action(ReplacementCardRequestActions.GetConfiguration)
    public getConfiguration(ctx: StateContext<ReplacementCardStateModel>): Observable<ReplacementCardRequestConfiguration> {
        return this.replacementCardRequestService.getConfiguration().pipe(tap((configuration) => {
            ctx.patchState({
                configuration: configuration,
            });
        }));
    }

    @Action(ReplacementCardRequestActions.Create)
    public create(ctx: StateContext<ReplacementCardStateModel>): Observable<BaseValidationResult> {
        const model = ctx.getState().form.model;
        return this.replacementCardRequestService.create(model).pipe(tap((result) => {
            ctx.patchState({
                result: result,
            });

            if (!result.success) {
                ctx.dispatch(new SetFormDirty("replacementCard.form"));
            }
        }));
    }

    @Action(ReplacementCardRequestActions.ResetForm)
    public resetForm(ctx: StateContext<ReplacementCardStateModel>): void {
        ctx.setState(defaults);
    }
}
